/**
 * Scroll-restoration policy for SPA navigation.
 *
 * React Router's default behaviour preserves the scroll position
 * across navigations, which makes sense at the framework level but
 * surprises every user on a marketing/content site: scroll halfway
 * down Landing, click a blog post in the nav, the post renders but
 * you're stuck at y=1200 looking at the middle of the article.
 *
 * Policy implemented here:
 *   PUSH or REPLACE (clicked a link / programmatic navigate)
 *     -> scroll to top (unless there's a hash anchor, e.g. #pricing)
 *   POP (back/forward button)
 *     -> preserve scroll. The browser already restores the previous
 *        scroll position correctly and overriding it is hostile UX.
 *   Same-path (clicked "Home" while already on /)
 *     -> still scroll to top — matches user expectation of "click
 *        the home link to get to the top of the page".
 *
 * Wired once at app boot from main.tsx via the same router-subscribe
 * mechanism that `trackPageViews` uses.
 */

type RouterLike = {
  subscribe: (fn: (state: { location: { pathname: string; hash: string }; historyAction: string }) => void) => () => void
  state: { location: { pathname: string; hash: string } }
}

declare global {
  interface Window {
    /** StrictMode + HMR guard so we don't subscribe twice. */
    __dqScrollBound?: boolean
  }
}

export function enableScrollToTopOnNavigate(router: RouterLike): void {
  if (typeof window === 'undefined') return
  if (window.__dqScrollBound) return
  window.__dqScrollBound = true

  router.subscribe((state) => {
    // Back/forward — let the browser handle scroll restoration.
    if (state.historyAction === 'POP') return

    // Hash anchor navigations (e.g. /#pricing). Let the in-page
    // scroll-to-element behaviour run; jumping to top first would
    // visibly flash before the anchor scroll lands.
    if (state.location.hash) return

    // Defer one frame so React commits the new route's DOM before we
    // scroll — otherwise long-page routes can scroll past their own
    // bottom and snap back.
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior })
    })
  })
}
